import { WitzyMark } from '@components/branding/WitzyLogo';
import { cn } from '@lib/utils';
import { useAuthStore } from '@stores/auth.store';
import { Avatar, Tooltip } from '@ui/index';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Building2,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Package,
  Scale,
  Settings,
  ShieldCheck,
  User,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

/**
 * Super-admin shell — completely separate from the customer-facing
 * `AppLayout`. Own sidebar (admin-only sections), own topbar with a
 * purple identity badge and a "Back to app" escape hatch.
 *
 * No project switcher, no wallet widget, no notification bell — those
 * are customer concerns. Admins live in a different cognitive mode and
 * the chrome reflects that.
 */

interface AdminNavItem {
  icon: LucideIcon;
  label: string;
  href: string;
}

// Order is operator-flow: triage first (Overview → Businesses → At-risk),
// then ops (Reconciliation), then catalog (Plans), then read-only
// (Analytics), then settings.
const ADMIN_NAV: AdminNavItem[] = [
  { icon: LayoutDashboard, label: 'Overview', href: '/admin' },
  { icon: Building2, label: 'Businesses', href: '/admin/businesses' },
  { icon: AlertTriangle, label: 'At risk', href: '/admin/at-risk' },
  { icon: Scale, label: 'Reconciliation', href: '/admin/reconciliation' },
  { icon: Package, label: 'Plans', href: '/admin/plans' },
  { icon: BarChart3, label: 'Analytics', href: '/admin/analytics' },
  { icon: Settings, label: 'Settings', href: '/admin/settings' },
];

export function AdminLayout(): JSX.Element {
  const location = useLocation();

  const isActive = (href: string): boolean =>
    href === '/admin'
      ? location.pathname === '/admin'
      : location.pathname.startsWith(href);

  return (
    <div className="flex h-screen overflow-hidden bg-apple-canvas">
      {/* Sidebar */}
      <aside className="flex h-screen w-60 shrink-0 flex-col border-r border-apple-divider bg-white">
        {/* Brand + admin badge */}
        <div className="flex h-16 shrink-0 items-center gap-2.5 border-b border-apple-divider px-4">
          <Link to="/admin" className="flex h-8 w-8 items-center justify-center">
            <WitzyMark size={28} />
          </Link>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-purple-600">
              Witzy · platform
            </div>
            <div className="truncate text-[13px] font-semibold text-apple-label">
              Super admin
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <div className="flex flex-col gap-0.5">
            {ADMIN_NAV.map((item) => {
              const active = isActive(item.href);
              const Icon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'group flex items-center gap-3 rounded-xl px-3 py-2 transition-all duration-200 ease-apple',
                    active
                      ? 'bg-gradient-to-r from-purple-50 to-violet-50 border-l-2 border-purple-600'
                      : 'hover:bg-apple-gray-100',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-all duration-200',
                      active
                        ? 'bg-gradient-to-br from-purple-500 to-violet-600 text-white shadow-btn-purple'
                        : 'bg-apple-gray-100 text-apple-gray-600 group-hover:bg-apple-gray-200',
                    )}
                  >
                    <Icon size={16} strokeWidth={1.5} />
                  </span>
                  <span
                    className={cn(
                      'text-[14px]',
                      active
                        ? 'font-semibold text-apple-label'
                        : 'font-medium text-apple-gray-700 group-hover:text-apple-label',
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* Footer — back to customer app */}
        <div className="border-t border-apple-divider p-2">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-medium text-apple-gray-700 transition hover:bg-apple-gray-100 hover:text-apple-label"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            Back to app
          </Link>
        </div>
      </aside>

      {/* Right column */}
      <div className="flex flex-1 flex-col min-w-0">
        <AdminTopBar />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-content px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AdminTopBar — slim header. Purple identity badge on left, admin-user
// pill on right. No project switcher, no wallet, no notifications —
// those don't apply at the platform level.
// ---------------------------------------------------------------------------

function AdminTopBar(): JSX.Element {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const fullName = user
    ? `${user.first_name} ${user.last_name ?? ''}`.trim()
    : 'Admin';

  const handleLogout = (): void => {
    clearAuth();
    navigate('/login', { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-4 border-b border-apple-divider bg-white/80 px-8 backdrop-blur-md">
      {/* Identity badge — visually distinct so the operator always knows
          they're in admin context, not customer context. */}
      <div className="flex items-center gap-2.5 rounded-full bg-gradient-to-r from-purple-50 to-violet-50 px-3 py-1.5">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-violet-600 text-white">
          <ShieldCheck size={12} strokeWidth={2} />
        </span>
        <span className="text-[12px] font-semibold text-purple-700">
          Super admin console
        </span>
      </div>

      <div className="ml-auto">
        <AdminUserMenu
          fullName={fullName}
          email={user?.email ?? ''}
          avatarUrl={user?.avatar_url ?? undefined}
          onLogout={handleLogout}
        />
      </div>
    </header>
  );
}

function AdminUserMenu({
  fullName,
  email,
  avatarUrl,
  onLogout,
}: {
  fullName: string;
  email: string;
  avatarUrl?: string;
  onLogout: () => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Tooltip content="Admin account" side="bottom">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex items-center gap-2 rounded-full bg-apple-gray-100 px-1 py-1 transition hover:bg-apple-gray-200"
        >
          <Avatar name={fullName} src={avatarUrl} size="sm" />
          <span className="hidden text-[12px] font-semibold text-apple-label lg:inline">
            {fullName}
          </span>
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            className="mr-1 text-apple-gray-500"
          />
        </button>
      </Tooltip>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-xl border border-apple-divider bg-white shadow-card"
        >
          <div className="border-b border-apple-divider px-4 py-3">
            <div className="flex items-center gap-2.5">
              <Avatar name={fullName} src={avatarUrl} size="md" />
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold text-apple-label">
                  {fullName}
                </div>
                <div className="truncate text-[11px] text-apple-gray-600">
                  {email}
                </div>
                <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-purple-600">
                  Super admin
                </div>
              </div>
            </div>
          </div>
          <Link
            to="/profile"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-[13px] text-apple-label transition hover:bg-apple-gray-50"
          >
            <User size={14} strokeWidth={1.75} className="text-apple-gray-600" />
            Profile
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
            className="flex w-full items-center gap-3 border-t border-apple-divider px-4 py-2.5 text-left text-[13px] text-rose-600 transition hover:bg-rose-50"
          >
            <LogOut size={14} strokeWidth={1.75} />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  );
}
